import { computed, type ComputedRef } from 'vue';
import {
  createNxMemorySettings,
  useNxSettings,
  type NxSettingsDocument,
} from '../../Settings/settingsSource';
import type { NxSavedTableFilter } from '../filterModel';
import { filterSetsFromSettings, filterSetToSettings, newFilterSetId } from '../tableSettings';

/**
 * Named filter sets of one table (I-2510). With a `settingsKey` they live in
 * that key of the user's settings, `filter_sets` (a map by id), through the
 * kit's settings contract — so they follow the account. Without one they live
 * as long as the table. Saving under an existing name replaces that set.
 */
export function useSavedTableFilters(params: { settingsKey: () => string | undefined }): {
  savedFilters: ComputedRef<NxSavedTableFilter[]>;
  saveFilter: (filter: Omit<NxSavedTableFilter, 'id'>) => NxSavedTableFilter;
  deleteFilter: (id: string) => void;
} {
  const provided = useNxSettings();
  const own = createNxMemorySettings();
  const OWN_KEY = 'filters';

  const target = () => {
    const key = params.settingsKey();
    return key ? { source: provided, key } : { source: own, key: OWN_KEY };
  };

  const savedFilters = computed(() => {
    const { source, key } = target();
    return filterSetsFromSettings(source.get(key).value);
  });

  function write(filterSets: NxSettingsDocument): void {
    const { source, key } = target();
    source.patch(key, { filter_sets: filterSets });
  }

  function saveFilter(filter: Omit<NxSavedTableFilter, 'id'>): NxSavedTableFilter {
    const existing = savedFilters.value.find((f) => f.name === filter.name);
    const entry: NxSavedTableFilter = { ...filter, id: newFilterSetId() };
    // A replaced set gets a new id: removing the old one in the same patch
    // leaves nothing of it behind, where merging into it would keep members
    // the new set no longer has.
    write({
      ...(existing ? { [existing.id]: null } : {}),
      [entry.id]: filterSetToSettings(filter),
    });
    return entry;
  }

  function deleteFilter(id: string): void {
    write({ [id]: null });
  }

  return { savedFilters, saveFilter, deleteFilter };
}
